import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import { wsClient } from '../../api/websocket';

const ACCEPTED_TYPES = '.pdf,.png,.jpg,.jpeg,.docx';
const MAX_SIZE_MB = 50;

export default function CatalogConfig() {
  const { t } = useTranslation();
  const settings = useSettings();
  const { token: authToken } = useAuth();
  const authHeaders = authToken ? { Authorization: `Bearer ${authToken}` } : {};
  const fileInputRef = useRef(null);

  const [providerInfo, setProviderInfo] = useState(null);
  const [imports, setImports] = useState([]);
  const [file, setFile] = useState(null);
  const [provider, setProvider] = useState('gemini');
  const [uploading, setUploading] = useState(false);
  const [activeImport, setActiveImport] = useState(null);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const fetchProviders = useCallback(async () => {
    try {
      const res = await fetch('/api/catalog/providers');
      const data = await res.json();
      setProviderInfo(data);
      if (data.providers?.length > 0) setProvider(data.providers[0].id);
    } catch {}
  }, []);

  const fetchImports = useCallback(async () => {
    try {
      const res = await fetch('/api/catalog/imports');
      if (res.ok) {
        const data = await res.json();
        setImports(data.imports || []);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchProviders();
    fetchImports();
  }, [fetchProviders, fetchImports]);

  // Subscribe to active import progress
  useEffect(() => {
    if (!activeImport) return;

    wsClient.send('catalog:subscribe', { importId: activeImport });

    const unsubProgress = wsClient.on('catalog:progress', (payload) => {
      if (payload.importId === activeImport) {
        setProgress(payload);
      }
    });

    const unsubComplete = wsClient.on('catalog:complete', (payload) => {
      if (payload.importId === activeImport) {
        setActiveImport(null);
        setProgress(null);
        setFile(null);
        fetchImports();
      }
    });

    const unsubError = wsClient.on('catalog:error', (payload) => {
      if (payload.importId === activeImport) {
        setActiveImport(null);
        setProgress(null);
        setError(payload.error);
        fetchImports();
      }
    });

    return () => { unsubProgress(); unsubComplete(); unsubError(); };
  }, [activeImport, fetchImports]);

  function validateAndSet(f) {
    setError(null);
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(t('catalog.fileTooLarge', { max: MAX_SIZE_MB }));
      return;
    }
    setFile(f);
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('provider', provider);
      formData.append('workspace_path', settings.workspacePath || '.');

      const res = await fetch('/api/catalog/upload', { method: 'POST', headers: authHeaders, body: formData });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      setActiveImport(data.importId);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(importId) {
    try {
      await fetch(`/api/catalog/imports/${importId}`, { method: 'DELETE' });
      fetchImports();
    } catch {}
  }

  async function handleCancel() {
    if (!activeImport) return;
    await fetch(`/api/catalog/imports/${activeImport}/cancel`, { method: 'POST' });
    setActiveImport(null);
    setProgress(null);
    fetchImports();
  }

  const pct = progress?.totalPages > 0
    ? Math.round((progress.page / progress.totalPages) * 100)
    : 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* Status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
        <span
          style={{
            width: 8, height: 8, borderRadius: '50%',
            background: imports.length > 0 ? 'var(--success, #22c55e)' : 'var(--text-tertiary)',
          }}
        />
        <span style={{ color: imports.length > 0 ? 'var(--success, #22c55e)' : 'var(--text-secondary)' }}>
          {imports.length > 0
            ? t('catalog.importCount', { count: imports.length })
            : t('catalog.noImports')}
        </span>
      </div>

      {/* Error */}
      {error && <div className="api-key-error">{error}</div>}

      {/* Active import progress */}
      {activeImport && progress && (
        <div style={{
          padding: '0.75rem',
          background: 'var(--background)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
            <span>{t('catalog.processing')}</span>
            <span>{progress.page} / {progress.totalPages}</span>
          </div>
          <div style={{
            height: 6, background: 'var(--bg-secondary, #eee)',
            borderRadius: 3, overflow: 'hidden',
          }}>
            <div style={{
              height: '100%', width: `${pct}%`,
              background: 'var(--primary, #4a9eff)',
              borderRadius: 3, transition: 'width 0.3s',
            }} />
          </div>
          <button
            className="api-key-delete-btn"
            onClick={handleCancel}
            style={{ marginTop: '0.5rem', fontSize: '0.8125rem' }}
          >
            {t('catalog.cancel')}
          </button>
        </div>
      )}

      {/* Upload zone (only when no active import) */}
      {!activeImport && providerInfo?.available && (
        <>
          <div
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files[0]) validateAndSet(e.dataTransfer.files[0]);
            }}
            style={{
              border: `2px dashed ${dragOver || file ? 'var(--primary, #4a9eff)' : 'var(--border)'}`,
              borderStyle: file ? 'solid' : 'dashed',
              borderRadius: 'var(--radius)',
              padding: '1.5rem',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              fontSize: '0.875rem',
              color: 'var(--text-secondary)',
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_TYPES}
              onChange={(e) => e.target.files[0] && validateAndSet(e.target.files[0])}
              hidden
            />
            {file ? (
              <div>
                <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{file.name}</span>
                <span style={{ marginLeft: '0.5rem' }}>({(file.size / 1024 / 1024).toFixed(1)} MB)</span>
              </div>
            ) : (
              <span>{t('catalog.dropOrClick')}</span>
            )}
          </div>

          {file && (
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <select
                value={provider}
                onChange={(e) => setProvider(e.target.value)}
                className="api-key-input"
                style={{ flex: 'none', width: 'auto' }}
              >
                {providerInfo.providers.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
              <button
                className="api-key-save-btn"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? t('catalog.uploading') : t('catalog.startImport')}
              </button>
            </div>
          )}
        </>
      )}

      {!providerInfo?.available && (
        <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
          {t('catalog.noVisionProvider')}
        </div>
      )}

      {/* Import list */}
      {imports.length > 0 && (
        <div>
          <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
            {t('catalog.importedDocuments')}
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {imports.map((imp) => (
              <div
                key={imp.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '0.5rem 0.75rem',
                  background: 'var(--background)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontSize: '0.8125rem',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                    {imp.filename || imp.title}
                  </span>
                  <span style={{
                    fontSize: '0.7rem',
                    padding: '0.125rem 0.375rem',
                    borderRadius: '4px',
                    background: imp.status === 'completed'
                      ? 'var(--success-bg, rgba(34, 197, 94, 0.1))'
                      : imp.status === 'failed'
                        ? 'var(--error-bg, rgba(220, 38, 38, 0.1))'
                        : 'var(--warning-bg, rgba(245, 158, 11, 0.1))',
                    color: imp.status === 'completed'
                      ? 'var(--success)'
                      : imp.status === 'failed'
                        ? 'var(--error)'
                        : 'var(--warning)',
                  }}>
                    {imp.status}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {imp.total_pages && (
                    <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)' }}>
                      {imp.processed_pages}/{imp.total_pages} {t('catalog.pages')}
                    </span>
                  )}
                  <button
                    onClick={() => handleDelete(imp.id)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--error, #dc2626)', fontSize: '0.75rem',
                      padding: '0.125rem 0.25rem',
                    }}
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
